import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { logout, getCurrentUser } from "../services/authService";

const API_URL = process.env.REACT_APP_API_URL + "/api/auth";

export default function ProfileDrawer({ onClose }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeSection, setActiveSection] = useState(null); // "username" | "email" | "password" | "delete"

  const [usernameInput, setUsernameInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [message, setMessage] = useState({ text: "", error: false });
  const [deleteCountdown, setDeleteCountdown] = useState(null);

  useEffect(() => {
    getCurrentUser().then(setUser);
  }, []);

  const showMessage = (text, error = false) => setMessage({ text, error });
  const clearMessage = () => setMessage({ text: "", error: false });

  const toggleSection = (section) => {
    const closing = activeSection === section;
    setActiveSection(closing ? null : section);
    clearMessage();
    if (section === "delete" && !closing) {
      setDeleteCountdown(5);
    } else if (section === "delete" && closing) {
      setDeleteCountdown(null);
    }
  };

  useEffect(() => {
    if (deleteCountdown === null || deleteCountdown <= 0) return;
    const timer = setTimeout(() => setDeleteCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [deleteCountdown]);

  const handleUpdateUsername = async () => {
    if (!usernameInput.trim()) return showMessage("Username is required", true);
    const res = await fetch(`${API_URL}/me/username`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ username: usernameInput }),
    });
    const data = await res.json();
    if (!res.ok) return showMessage(data.error, true);
    setUser((u) => ({ ...u, username: data.username }));
    setUsernameInput("");
    setActiveSection(null);
    showMessage("Username updated");
  };

  const handleUpdateEmail = async () => {
    if (!emailInput.trim()) return showMessage("Email is required", true);
    const res = await fetch(`${API_URL}/me/email`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email: emailInput }),
    });
    const data = await res.json();
    if (!res.ok) return showMessage(data.error, true);
    setUser((u) => ({ ...u, email: data.email }));
    setEmailInput("");
    setActiveSection(null);
    showMessage("Email updated");
  };

  const handleUpdatePassword = async () => {
    if (newPassword.length < 8) return showMessage("Password must be at least 8 characters", true);
    if (newPassword !== confirmNewPassword) return showMessage("Passwords do not match", true);
    const res = await fetch(`${API_URL}/me/password`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
    const data = await res.json();
    if (!res.ok) return showMessage(data.error, true);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
    setActiveSection(null);
    showMessage("Password updated");
  };

  const handleDeleteAccount = async () => {
    const res = await fetch(`${API_URL}/me`, {
      method: "DELETE",
      credentials: "include",
    });
    if (res.ok) {
      navigate("/");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <>
      <div className="profile-drawer-overlay" onClick={onClose} />
      <div className="profile-drawer">

        {/* Header */}
        <div className="profile-drawer-header">
          <div className="profile-drawer-user">
            <div className="profile-drawer-avatar">
              {user?.username?.[0]?.toUpperCase() ?? "?"}
            </div>
            <div>
              <div className="profile-drawer-name">{user?.username}</div>
              <div className="profile-drawer-email">{user?.email}</div>
            </div>
          </div>
          <button className="profile-drawer-close" onClick={onClose}>✕</button>
        </div>

        {message.text && (
          <p className={`profile-drawer-message ${message.error ? "error" : "success"}`}>
            {message.text}
          </p>
        )}

        {/* Account Section */}
        <div className="profile-drawer-section">
          <div className="profile-drawer-section-title">Account</div>

          <div className="profile-drawer-item">
            <button className="profile-drawer-item-btn" onClick={() => toggleSection("username")}>
              Change username
            </button>
            {activeSection === "username" && (
              <div className="profile-drawer-inline-form">
                <input
                  type="text"
                  placeholder="New username"
                  value={usernameInput}
                  onChange={(e) => setUsernameInput(e.target.value)}
                  autoComplete="off"
                />
                <div className="profile-drawer-inline-actions">
                  <button className="btn-sm btn-primary-sm" onClick={handleUpdateUsername}>Save</button>
                  <button className="btn-sm btn-ghost-sm" onClick={() => toggleSection("username")}>Cancel</button>
                </div>
              </div>
            )}
          </div>

          <div className="profile-drawer-item">
            <button className="profile-drawer-item-btn" onClick={() => toggleSection("email")}>
              Change email
            </button>
            {activeSection === "email" && (
              <div className="profile-drawer-inline-form">
                <input
                  type="email"
                  placeholder="New email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                />
                <div className="profile-drawer-inline-actions">
                  <button className="btn-sm btn-primary-sm" onClick={handleUpdateEmail}>Save</button>
                  <button className="btn-sm btn-ghost-sm" onClick={() => toggleSection("email")}>Cancel</button>
                </div>
              </div>
            )}
          </div>

          <div className="profile-drawer-item">
            <button className="profile-drawer-item-btn" onClick={() => toggleSection("password")}>
              Change password
            </button>
            {activeSection === "password" && (
              <div className="profile-drawer-inline-form">
                <input
                  type="password"
                  placeholder="Current password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
                <input
                  type="password"
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                />
                <div className="profile-drawer-inline-actions">
                  <button className="btn-sm btn-primary-sm" onClick={handleUpdatePassword}>Save</button>
                  <button className="btn-sm btn-ghost-sm" onClick={() => toggleSection("password")}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>


        {/* Danger Section */}
        <div className="profile-drawer-section">
          <div className="profile-drawer-section-title danger">Danger zone</div>
          <div className="profile-drawer-item">
            <button className="profile-drawer-item-btn danger" onClick={() => toggleSection("delete")}>
              <span className={`profile-drawer-chevron ${activeSection === "delete" ? "open" : ""}`}>▶</span>
              Delete account
            </button>
            {activeSection === "delete" && (
              <div className="profile-drawer-inline-form">
                <p className="profile-drawer-delete-warning">
                  This will permanently delete your account and all your data. This cannot be undone.
                </p>
                <div className="profile-drawer-inline-actions">
                  <button
                    className="btn-sm btn-danger-sm"
                    onClick={handleDeleteAccount}
                    disabled={deleteCountdown > 0}
                  >
                    {deleteCountdown > 0 ? `Wait ${deleteCountdown}s...` : "Yes, delete my account"}
                  </button>
                  <button className="btn-sm btn-ghost-sm" onClick={() => toggleSection("delete")}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="profile-drawer-footer">
          <button className="profile-drawer-signout" onClick={handleLogout}>Sign out</button>
        </div>

      </div>
    </>
  );
}
